import { useIntl } from "react-intl";
import { Dialog, DialogContent, IconButton, Typography, Stack, useTheme, useMediaQuery, Box } from "@mui/material";
import { Close } from "@mui/icons-material";
import { format } from "date-fns";

import { ReactComponent as CalendarFilled } from "assets/icons/dataIcon/calendar-filled.svg";

import { ButtonContained } from "components/button/ButtonContained";
import { SlideTransition } from "components/SlideTransition";

import { ActivityStatus, TaskActivityResponse } from "user-journey/consts/journeyActivity";

import { stripHtmlTags } from "utils/string-utils";

import { buttonColor } from "./journey-activity/JourneyActivity.styled";

interface TaskActivityDetailsProps {
	activity: TaskActivityResponse;
	open: boolean;
	onClose: () => void;
	onContinue: () => void;
	loading: boolean;
}

export const TaskActivityDetails = ({ open, activity, onClose, onContinue, loading }: TaskActivityDetailsProps) => {
	const { formatMessage } = useIntl();

	const theme = useTheme();
	const matchesMobile = useMediaQuery(theme.breakpoints.down("sm"));

	return (
		<Dialog
			open={open}
			onClose={onClose}
			TransitionComponent={matchesMobile ? SlideTransition : undefined}
			PaperProps={{
				sx: {
					borderRadius: theme.spacing(2),
					maxHeight: `calc(100% - ${theme.spacing(8)})`,

					...(matchesMobile && {
						m: 0,
						position: "fixed",
						bottom: 0,
						left: 0,
						right: 0,
						maxHeight: "90dvh",
						height: "auto",
						width: "100%",
						maxWidth: "100%",
						borderBottomLeftRadius: 0,
						borderBottomRightRadius: 0,
					}),
				},
			}}
		>
			<DialogContent
				sx={{
					position: "relative",
					px: 5,
					py: 4,

					...(matchesMobile && {
						px: 4,
						overflowY: "auto",
						maxHeight: "inherit",
					}),
				}}
			>
				<IconButton
					onClick={onClose}
					color="primary"
					size="medium"
					sx={{
						p: 0,
						position: "absolute",
						top: theme.spacing(4),
						right: theme.spacing(5),

						...(matchesMobile && {
							right: (theme) => theme.spacing(4),
						}),
					}}
				>
					<Close sx={{ color: theme.palette.grey["500"] }} />
				</IconButton>

				{/* TASK ACTIVITY DETAILS */}
				<Stack gap={2} mb={6}>
					<Typography
						fontWeight={700}
						variant="md"
						color="grey.700"
						width={`calc(100% - ${theme.spacing(8)})`}
					>
						{activity.title}
					</Typography>

					<Box
						title={stripHtmlTags(activity.description as string)}
						sx={{
							width: "100%",
							...theme.typography.sm,
							color: "dirtyWhite.2000",
							textOverflow: "ellipsis",
							overflow: "hidden",
							whiteSpace: "pre-line",

							"& .communication-message": {
								"& *": {
									color: "inherit !important",
								},
							},
							"& .custom-variable": {
								fontWeight: 700,
							},
						}}
					>
						<div
							className="communication-message"
							dangerouslySetInnerHTML={{
								__html: activity.description as string,
							}}
						/>
					</Box>

					<Stack direction="row" alignItems="center">
						<CalendarFilled />
						<Typography variant="sm" ml={1}>
							{formatMessage({ id: "GLOBAL.deadline" })}
						</Typography>
						<Typography variant="sm" fontWeight={700} ml={2}>
							{format(new Date(activity.dueDate), "EEEE, dd MMMM yyyy")}
						</Typography>
					</Stack>
				</Stack>

				<ButtonContained
					fullWidth
					loading={loading}
					color={buttonColor[activity.status]}
					onClick={onContinue}
					disabled={
						[ActivityStatus.LOCKED, ActivityStatus.FAILED, ActivityStatus.COMPLETED].includes(
							activity.status,
						) || loading
					}
				>
					{formatMessage({
						id: "DASHBOARD.employee-journey.activity_type.task.request-completion",
					})}
				</ButtonContained>
			</DialogContent>
		</Dialog>
	);
};
